import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  LocalParking as ParkingIcon,
} from '@mui/icons-material';
import { Vehicle, ParkingSpot } from '../../types';
import { api } from '../../services/api';
import CurrentLayoutDisplay from '../CurrentLayoutDisplay';

interface ParkingMapModalProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onReassign: (vehicleId: string, newSpotId: string) => void;
}

const ParkingMapModal: React.FC<ParkingMapModalProps> = ({
  vehicle,
  open,
  onClose,
  onReassign,
}) => {
  const [parkingSpots, setParkingSpots] = useState<ParkingSpot[]>([]);
  const [currentLayout, setCurrentLayout] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [showReassignMode, setShowReassignMode] = useState(false);

  useEffect(() => {
    if (open && vehicle) {
      loadParkingData();
    }
  }, [open, vehicle]);

  const loadParkingData = async () => {
    setLoading(true);
    try {
      // Load parking spots
      const spotsResponse = await api.get('/parking-spots');
      setParkingSpots(spotsResponse.data.data || []);

      // Load ALL parking layouts (not just active ones) to find the correct plan
      const layoutsResponse = await api.get('/parking-layouts');
      const layouts = layoutsResponse.data.data || [];
      
      // Find the layout that contains the vehicle's parking spot
      let targetLayout = null;
      if (vehicle?.place_parking_id) {
        // Find which layout contains this parking spot
        for (const layout of layouts) {
          const layoutResponse = await api.get(`/parking-layouts/${layout.id}`);
          const fullLayoutData = layoutResponse.data;
          const layoutData = fullLayoutData.data || fullLayoutData;
          
          const settings = layoutData.settings || {};
          const visualSpots = settings.visual_spots || [];
          
          // Check if this layout contains the vehicle's parking spot
          const hasVehicleSpot = visualSpots.some((visualSpot: any) => 
            visualSpot.assigned_spot_id && 
            vehicle.place_parking_id &&
            visualSpot.assigned_spot_id.toString() === vehicle.place_parking_id.toString()
          );
          
          if (hasVehicleSpot) {
            targetLayout = layout;
            break;
          }
        }
      }
      
      // If no specific layout found, use the first active layout
      if (!targetLayout) {
        targetLayout = layouts.find((layout: any) => layout.is_active);
      }
      
      if (targetLayout) {
        const layoutResponse = await api.get(`/parking-layouts/${targetLayout.id}`);
        const fullLayoutData = layoutResponse.data;
        const layoutData = fullLayoutData.data || fullLayoutData;
        
        const settings = layoutData.settings || {};
        const visualSpots = settings.visual_spots || [];
        const visualElements = settings.visual_elements || [];
        
        const layoutWithVisualData = {
          ...targetLayout,
          spots: visualSpots,
          elements: visualElements,
          canvas: {
            width: layoutData.canvas_width || 1400,
            height: layoutData.canvas_height || 900,
            background_color: layoutData.background_color || '#ffffff',
          }
        };
        
        setCurrentLayout(layoutWithVisualData);
      }
    } catch (error) {
      console.error('Error loading parking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSpotColor = (visualSpot: any) => {
    if (!visualSpot.assigned_spot_id) return '#e0e0e0';
    
    const realSpot = parkingSpots.find(spot => spot.id.toString() === visualSpot.assigned_spot_id.toString());
    if (!realSpot) return '#e0e0e0';
    
    // Check if this is the vehicle's current parking spot
    const isVehicleSpot = vehicle?.place_parking_id && 
      realSpot.id.toString() === vehicle.place_parking_id.toString();
    
    if (isVehicleSpot) {
      return '#9c27b0'; // Purple highlight for the selected vehicle's spot
    }
    
    if (realSpot.is_occupied) {
      return '#f44336'; // Red for occupied
    }
    
    // Return normal color based on spot type
    const SPOT_TYPE_COLORS = {
      regular: '#2196f3',
      maintenance: '#ff9800',
      washing: '#9c27b0',
      premium: '#4caf50',
    };
    
    return SPOT_TYPE_COLORS[realSpot.spot_type as keyof typeof SPOT_TYPE_COLORS] || '#e0e0e0';
  };

  const getSpotText = (visualSpot: any) => {
    if (!visualSpot.assigned_spot_id) return '';
    
    const realSpot = parkingSpots.find(spot => spot.id.toString() === visualSpot.assigned_spot_id.toString());
    if (!realSpot) return 'N/A';
    
    // Check if this is the vehicle's current parking spot
    const isVehicleSpot = vehicle?.place_parking_id && 
      realSpot.id.toString() === vehicle.place_parking_id.toString();
    
    if (isVehicleSpot) {
      return `★ ${vehicle?.registration_number}`; // Add star indicator for the selected vehicle
    }
    
    if (realSpot.is_occupied && realSpot.vehicle) {
      return realSpot.vehicle.registration_number;
    }
    
    return realSpot.spot_number || 'N/A';
  };

  const getVehicleSpot = () => {
    if (!vehicle?.place_parking_id) return null;
    return parkingSpots.find(spot => spot.id === vehicle.place_parking_id);
  };

  const vehicleSpot = getVehicleSpot();

  const handleSpotClick = (spotData: any) => {
    if (!showReassignMode || !vehicle) return;
    
    // Find the real parking spot
    const realSpot = parkingSpots.find(spot => spot.id.toString() === spotData.assigned_spot_id?.toString());
    if (!realSpot) return;
    
    // Don't allow reassigning to the same spot
    if (realSpot.id === vehicle.place_parking_id) return;
    
    // Don't allow reassigning to an occupied spot
    if (realSpot.is_occupied) return;
    
    // Reassign the vehicle
    handleReassign(realSpot.id.toString());
  };

  const handleReassign = async (newSpotId: string) => {
    if (!vehicle) return;
    
    setReassigning(true);
    try {
      await onReassign(vehicle.id.toString(), newSpotId);
      setShowReassignMode(false);
      // Reload parking data to reflect changes
      await loadParkingData();
    } catch (error) {
      console.error('Error reassigning vehicle:', error);
    } finally {
      setReassigning(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' },
          height: { xs: '100vh', sm: 'auto' }
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="span" sx={{ 
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              wordBreak: 'break-word'
            }}>
              Emplacement du véhicule - {vehicle?.registration_number}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              display: 'block',
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}>
              Le véhicule sélectionné est mis en évidence en violet sur le plan
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ 
        p: { xs: 1, sm: 2 },
        '&.MuiDialogContent-root': {
          paddingTop: { xs: 1, sm: 2 }
        }
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 1, sm: 2 } }}>
            {/* Vehicle Parking Info */}
            {vehicleSpot ? (
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Typography variant="h6" color="primary" sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      <ParkingIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      Place de parking assignée
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                      {!showReassignMode ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowReassignMode(true)}
                          disabled={reassigning}
                          fullWidth
                        >
                          Réassigner
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowReassignMode(false)}
                          disabled={reassigning}
                          fullWidth
                        >
                          Annuler
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 2, sm: 3 }, 
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Numéro de place
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {vehicleSpot.spot_number}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Emplacement
                      </Typography>
                      <Typography variant="body1">
                        {vehicleSpot.location}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Type de place
                      </Typography>
                      <Chip 
                        label={vehicleSpot.spot_type}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Statut
                      </Typography>
                      <Chip 
                        label={vehicleSpot.is_occupied ? 'Occupée' : 'Disponible'}
                        size="small"
                        color={vehicleSpot.is_occupied ? 'error' : 'success'}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Typography variant="h6" color="primary" sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      <ParkingIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      Aucune place assignée
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowReassignMode(true)}
                      disabled={reassigning}
                      fullWidth
                    >
                      Assigner une place
                    </Button>
                  </Box>
                  <Alert severity="info" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Ce véhicule n'est pas assigné à une place de parking. Cliquez sur "Assigner une place" puis sélectionnez une place disponible sur le plan.
                  </Alert>
                </CardContent>
              </Card>
            )}

            {/* Parking Layout Map */}
            {currentLayout && (
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Typography variant="h6" color="primary" sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                      Plan du parking
                    </Typography>
                    {showReassignMode && (
                      <Alert severity="info" sx={{ 
                        py: 0.5,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        width: { xs: '100%', sm: 'auto' }
                      }}>
                        {vehicle.parking_spot 
                          ? "Mode réassignation: Cliquez sur une place disponible pour réassigner le véhicule"
                          : "Mode assignation: Cliquez sur une place disponible pour assigner le véhicule"
                        }
                      </Alert>
                    )}
                  </Box>
                  
                  {/* Color Legend */}
                  <Box sx={{ 
                    mb: 2, 
                    p: { xs: 1.5, sm: 2 }, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1 
                  }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                      mb: 1,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}>
                      Légende des couleurs :
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: { xs: 2, sm: 3 }, 
                      flexWrap: 'wrap',
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#9c27b0', borderRadius: 0.5 }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Véhicule sélectionné
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#f44336', borderRadius: 0.5 }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Occupé
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#2196f3', borderRadius: 0.5 }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Disponible
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#e0e0e0', borderRadius: 0.5 }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Sans liaison
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ 
                    height: { xs: 300, sm: 500 }, 
                    position: 'relative',
                    overflow: 'auto'
                  }}>
                    <CurrentLayoutDisplay 
                      layout={currentLayout} 
                      parkingSpots={parkingSpots}
                      onSpotClick={showReassignMode ? handleSpotClick : undefined}
                      getSpotColor={getSpotColor}
                      getSpotText={getSpotText}
                    />
                    {reassigning && (
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                      }}>
                        <CircularProgress />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {!currentLayout && !loading && (
              <Alert severity="warning">
                Aucun plan de parking actuel disponible.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        p: { xs: 1, sm: 2 },
        justifyContent: 'center'
      }}>
        <Button 
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{ 
            minWidth: { xs: 'auto', sm: '120px' },
            py: { xs: 1.5, sm: 1 }
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParkingMapModal;
